/**
 * Collapsed "See more" preview on the public class landing page.
 * Measures how much of the instructions HTML stays visible, and keeps clipped controls
 * out of the tab order until the preview expands.
 */

/** Text lines shown before "See more" when instructions are long. */
export const COLLAPSED_INSTRUCTION_LINES = 3;
/** Height cap for the preview, so images and videos (which have no text lines) still collapse. */
export const COLLAPSED_INSTRUCTIONS_MAX_HEIGHT = 240;

/** Focusable controls inside the instructions. Clipped ones must leave the tab order. */
const INSTRUCTION_FOCUSABLE =
  'a[href], button, input, select, textarea, iframe, video, audio, [tabindex]';
/** Remembers a control's tabindex while it is clipped out of the tab order. */
export const CLIPPED_TABINDEX_ATTR = "data-ss-clipped-tabindex";

/**
 * While instructions are collapsed, controls fully below the visible clip leave the tab
 * order. Visible links stay reachable, then focus moves to See more. Expanding restores them.
 */
export const syncClippedInstructionFocus = (
  root: HTMLElement,
  collapsed: boolean
): void => {
  root.querySelectorAll(`[${CLIPPED_TABINDEX_ATTR}]`).forEach((node) => {
    const el = node as HTMLElement;
    const previous = el.getAttribute(CLIPPED_TABINDEX_ATTR) ?? "";
    if (previous) {
      el.setAttribute("tabindex", previous);
    } else {
      el.removeAttribute("tabindex");
    }
    el.removeAttribute(CLIPPED_TABINDEX_ATTR);
    el.inert = false;
  });
  if (!collapsed) {
    return;
  }
  const visibleBottom = root.getBoundingClientRect().bottom;
  root.querySelectorAll(INSTRUCTION_FOCUSABLE).forEach((node) => {
    const el = node as HTMLElement;
    const rect = el.getBoundingClientRect();
    if (rect.height < 1 && rect.width < 1) {
      return;
    }
    if (rect.top < visibleBottom - 1) {
      return;
    }
    el.setAttribute(CLIPPED_TABINDEX_ATTR, el.getAttribute("tabindex") ?? "");
    el.tabIndex = -1;
    el.inert = true;
  });
};

/** Bottom offset of each visual text line, measured from the top of `root`, in reading order. */
export const textLineBottoms = (root: HTMLElement): number[] => {
  const rootTop = root.getBoundingClientRect().top;
  const lines: { top: number; bottom: number }[] = [];
  const walker = document.createTreeWalker(root, NodeFilter.SHOW_TEXT);
  let node: Node | null = walker.nextNode();
  while (node) {
    if (node.textContent?.trim()) {
      const range = document.createRange();
      range.selectNodeContents(node);
      const rects = range.getClientRects();
      for (const rect of rects) {
        if (rect.height < 1 || rect.width < 1) {
          continue;
        }
        const match = lines.find((line) => Math.abs(line.top - rect.top) < 2);
        if (match) {
          match.bottom = Math.max(match.bottom, rect.bottom);
        } else {
          lines.push({ top: rect.top, bottom: rect.bottom });
        }
      }
    }
    node = walker.nextNode();
  }
  lines.sort((a, b) => a.top - b.top);
  return lines.map((line) => line.bottom - rootTop);
};
